// CredentialRecordsPanel — the learner's credential management surface.
// Mounted on: /learn (self), Parent Dashboard (per child).
// Features: PIN set/change, per-cert visibility toggle, view counter,
// profile photo upload, public page link, and the motivation copy.
//
// VISIBILITY RULES (settled):
//   University: always visible (no toggle shown)
//   Standard ≥70: visible, toggle OFF allowed
//   Standard <70: hidden, toggle ON allowed
//   Elite / Bible / Community: fully toggleable
//   NO tier badges displayed — the toggle reason is shown as helpful text,
//   never as "this is a lower-tier course"
import { useState, useEffect, useCallback } from 'react'
import {
  ShieldCheck, Eye, EyeOff, KeyRound, Upload, Loader2, ExternalLink,
  TrendingUp, Lock, Check,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

const G = '#d4a843'
const GR = '#4ade80'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'

interface CertRecord {
  id: string
  course_name: string
  mastery_score: number | null
  issued_at: string
  cert_type: string
  is_publicly_visible: boolean
  deep_view_count: number
  last_deep_view_at: string | null
  can_toggle: boolean
  toggle_reason: string | null
}

interface Props {
  userId: string
  isChild?: boolean
  publicSlug?: string | null
}


async function hashPinClient(pinStr: string, salt: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(pinStr + salt)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
}

export default function CredentialRecordsPanel({ userId, isChild = false, publicSlug }: Props) {
  const { user } = useAuth()
  const [certs, setCerts] = useState<CertRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [pinLoading, setPinLoading] = useState(false)
  const [pinMessage, setPinMessage] = useState<string | null>(null)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [hasPin, setHasPin] = useState(false)

  const loadCerts = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    const { data } = await supabase
      .from('university_certificates')
      .select(`
        id, issued_at, cert_type, mastery_score, is_publicly_visible,
        deep_view_count, last_deep_view_at,
        categories!category_id (name)
      `)
      .eq('user_id', userId)
      .order('issued_at', { ascending: false })
    setCerts((data || []).map((c: any) => ({
      id: c.id,
      course_name: c.categories?.name || 'Course',
      mastery_score: c.mastery_score,
      issued_at: c.issued_at,
      cert_type: c.cert_type,
      is_publicly_visible: c.is_publicly_visible,
      deep_view_count: c.deep_view_count || 0,
      last_deep_view_at: c.last_deep_view_at,
      can_toggle: c.cert_type !== 'university',
      toggle_reason: c.cert_type === 'university'
        ? 'University credentials are always visible on your public page.'
        : (c.mastery_score ?? 0) < 70 && c.cert_type === 'sage'
          ? 'Hidden because mastery is below 70. You can make it visible to show your learning journey.'
          : null,
    })))
    setLoading(false)
  }, [userId])

  useEffect(() => { loadCerts() }, [loadCerts])

  // Check if PIN is set
  useEffect(() => {
    if (!userId) return
    supabase
      .from('users')
      .select('verification_pin_hash, profile_photo_path')
      .eq('id', userId)
      .single()
      .then(({ data }) => {
        setHasPin(!!(data as any)?.verification_pin_hash)
        if ((data as any)?.profile_photo_path) {
          // Generate a signed URL for display
          supabase.functions.invoke('get-verify-data', {
            body: { slug: publicSlug || userId },
          }).then(({ data: vd }: any) => {
            if (vd?.learner?.photo_url) setPhotoUrl(vd.learner.photo_url)
          }).catch(() => {})
        }
      })
  }, [userId, publicSlug])

  // ── PIN management ──
  const setPin_ = async () => {
    if (pin.length !== 4 || pin !== pinConfirm) {
      setPinMessage('PINs must match and be exactly 4 digits.')
      return
    }
    setPinLoading(true)
    setPinMessage(null)
    try {
      const { data } = await supabase.functions.invoke('set-verification-pin', {
        body: { user_id: userId, new_pin: pin },
      })
      if (data?.error) throw new Error(data.error)
      setPinMessage(data?.message || 'PIN set successfully.')
      setHasPin(true)
      setPin('')
      setPinConfirm('')
    } catch (err: any) {
      setPinMessage(err.message || 'Failed to set PIN.')
    } finally {
      setPinLoading(false)
    }
  }

  // ── Toggle cert visibility ──
  const toggleVisibility = async (certId: string, current: boolean) => {
    const { error } = await supabase
      .from('university_certificates')
      .update({ is_publicly_visible: !current })
      .eq('id', certId)
    if (!error) {
      setCerts(prev => prev.map(c =>
        c.id === certId ? { ...c, is_publicly_visible: !current } : c
      ))
    }
  }

  // ── Photo upload ──
  const uploadPhoto = async (file: File) => {
    setPhotoUploading(true)
    try {
      const path = `${userId}/photo.${file.name.split('.').pop()}`
      const { error: uploadErr } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true })
      if (uploadErr) throw uploadErr

      await supabase
        .from('users')
        .update({ profile_photo_path: path })
        .eq('id', userId)

      // Get signed URL for display
      const { data: signed } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(path, 3600)
      setPhotoUrl(signed?.signedUrl ?? null)
    } catch (err: any) {
      console.error('Photo upload failed:', err)
    } finally {
      setPhotoUploading(false)
    }
  }

  const totalViews = certs.reduce((s, c) => s + c.deep_view_count, 0)

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: MU }}>
        <Loader2 size={18} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} />
        Loading credential records…
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ═══ MOTIVATION HEADER ═══ */}
      <div style={{
        background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.2)',
        borderRadius: 16, padding: '18px 22px',
      }}>
        <p style={{ fontSize: 13, color: TX, fontWeight: 700, margin: '0 0 6px', lineHeight: 1.5 }}>
          🎓 Your Permanent Learning Record
        </p>
        <p style={{ fontSize: 12, color: MU, margin: 0, lineHeight: 1.6 }}>
          Every lesson, quiz, and retry you complete is recorded permanently.
          When you earn a certificate, employers and universities can verify your
          actual work at your public credential page. Your effort here isn't just
          for you — it's evidence that lasts and opens doors.
        </p>
        {publicSlug && (
          <a
            href={`/credentials/${publicSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              marginTop: 10, color: G, fontWeight: 700, fontSize: 12, textDecoration: 'none',
            }}
          >
            <ExternalLink size={12} /> View your public credential page →
          </a>
        )}
      </div>

      {/* ═══ VIEW COUNTER ═══ */}
      {totalViews > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
          background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.15)',
          borderRadius: 12,
        }}>
          <TrendingUp size={16} color={BR ? '#0ea5e9' : G} />
          <p style={{ fontSize: 12.5, color: TX, margin: 0 }}>
            <strong>{totalViews}</strong> deep view{totalViews === 1 ? '' : 's'} of your learning record.
            {totalViews > 5 && ' If you only shared your PIN with a few places, consider changing it below.'}
          </p>
        </div>
      )}

      {/* ═══ PIN MANAGEMENT ═══ */}
      <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 16, padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <KeyRound size={16} color={G} />
          <p style={{ fontSize: 13, fontWeight: 700, color: TX, margin: 0 }}>
            Verification PIN {hasPin ? '(active)' : '(not set)'}
          </p>
        </div>
        <p style={{ fontSize: 11.5, color: MU, margin: '0 0 14px', lineHeight: 1.5 }}>
          A 4-digit PIN unlocks your full learning transcripts on your public page.
          Give it with your certificate to employers or universities you trust.
          Anyone with the certificate alone sees only the course and score.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text" inputMode="numeric" pattern="\d*" maxLength={4}
            value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder={hasPin ? 'New PIN' : 'PIN'}
            style={{
              width: 90, padding: '10px 14px', borderRadius: 10,
              border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.04)',
              color: TX, fontSize: 18, textAlign: 'center',
              fontFamily: "'Courier New', monospace", fontWeight: 700,
              letterSpacing: '4px', outline: 'none',
            }}
          />
          <input
            type="text" inputMode="numeric" pattern="\d*" maxLength={4}
            value={pinConfirm} onChange={e => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="Confirm"
            style={{
              width: 90, padding: '10px 14px', borderRadius: 10,
              border: `2px solid ${BR}`, background: 'rgba(255,255,255,0.04)',
              color: TX, fontSize: 18, textAlign: 'center',
              fontFamily: "'Courier New', monospace", fontWeight: 700,
              letterSpacing: '4px', outline: 'none',
            }}
          />
          <button
            onClick={setPin_}
            disabled={pinLoading || pin.length !== 4 || pin !== pinConfirm}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: pin.length === 4 && pin === pinConfirm ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)',
              color: pin.length === 4 && pin === pinConfirm ? '#06060c' : MU,
              fontWeight: 700, fontSize: 13,
            }}
          >
            {pinLoading ? '…' : hasPin ? 'Update PIN' : 'Set PIN'}
          </button>
        </div>
        {pinMessage && <p style={{ fontSize: 11, color: GR, margin: '10px 0 0' }}>{pinMessage}</p>}
      </div>

      {/* ═══ PROFILE PHOTO ═══ */}
      <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 16, padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Upload size={16} color={G} />
          <p style={{ fontSize: 13, fontWeight: 700, color: TX, margin: 0 }}>Profile Photo</p>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {photoUrl ? (
            <img src={photoUrl} alt="Profile" style={{ width: 72, height: 72, borderRadius: 16, objectFit: 'cover', border: '1px solid rgba(74,222,128,0.3)' }} />
          ) : (
            <div style={{ width: 72, height: 72, borderRadius: 16, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MU, fontSize: 28 }}>
              {isChild ? '👤' : '📷'}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 200 }}>
            <p style={{ fontSize: 11.5, color: MU, margin: '0 0 10px', lineHeight: 1.5 }}>
              This photo appears on your public credential page when someone enters your PIN —
              it confirms you're the person who earned the certificates. It can only be
              changed by contacting Still Waters Academy.
            </p>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 10, border: `1px solid ${BR}`,
              cursor: 'pointer', color: G, fontWeight: 700, fontSize: 12,
            }}>
              {photoUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {photoUrl ? 'Replace photo' : 'Upload photo'}
              <input
                type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
              />
            </label>
          </div>
        </div>
      </div>

      {/* ═══ CERTIFICATE VISIBILITY TOGGLES ═══ */}
      {certs.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 16, padding: '20px 22px' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: TX, margin: '0 0 6px' }}>
            Public Page Visibility
          </p>
          <p style={{ fontSize: 11.5, color: MU, margin: '0 0 16px', lineHeight: 1.5 }}>
            Choose which certificates appear on your public page. University credentials
            are always visible. You can show or hide personal interest courses.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {certs.map(cert => (
              <div key={cert.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', background: 'rgba(255,255,255,0.02)',
                borderRadius: 10, border: '1px solid rgba(255,255,255,0.04)',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: TX, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cert.course_name}
                  </p>
                  <p style={{ fontSize: 10.5, color: MU2, margin: '2px 0 0' }}>
                    {cert.mastery_score}% mastery · {new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {cert.deep_view_count > 0 && ` · ${cert.deep_view_count} views`}
                  </p>
                  {cert.toggle_reason && (
                    <p style={{ fontSize: 10, color: MU, margin: '3px 0 0', fontStyle: 'italic' }}>
                      {cert.toggle_reason}
                    </p>
                  )}
                </div>
                {cert.can_toggle ? (
                  <button
                    onClick={() => toggleVisibility(cert.id, cert.is_publicly_visible)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 12px', borderRadius: 8,
                      border: `1px solid ${cert.is_publicly_visible ? 'rgba(74,222,128,0.3)' : BR}`,
                      background: cert.is_publicly_visible ? 'rgba(74,222,128,0.1)' : 'transparent',
                      color: cert.is_publicly_visible ? GR : MU,
                      fontWeight: 700, fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    {cert.is_publicly_visible ? <Eye size={11} /> : <EyeOff size={11} />}
                    {cert.is_publicly_visible ? 'Visible' : 'Hidden'}
                  </button>
                ) : (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 8,
                    background: 'rgba(212,168,67,0.08)', color: G,
                    fontWeight: 700, fontSize: 10,
                  }}>
                    <Lock size={10} /> Always visible
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}